import { RouteProp, useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { PlayerIconPng } from '../../assets';
import {
	DuelChipStackPng,
	DuelCompleteTrophyJpg,
	DuelCrownMedallionPng,
	DuelIconCrownSvg,
	DuelRatingShieldPng,
	DuelScoreShieldPng,
	DuelSettledBannerJpg,
	DuelStarPng,
	DuelXpJpg,
} from '../../assets/duels';
import type {
	DuelResultLeaderboardEntryDto,
	DuelResultResponse,
	DuelSnapshotDto,
} from '../../services/api';
import {
	duelResultBlur,
	duelResultFocus,
	duelResultRetry,
	duelsScreenMounted,
	duelsScreenUnmounted,
} from '../../store/duels/duelsActions';
import {
	selectDuelResult,
	selectDuelResultErrorKey,
	selectDuelSnapshot,
} from '../../store/duels/duelsReducer';
import { getAvatarUrl } from '../../utils/axiosInstance';
import { GoldGradientText } from '../Components/common/GoldGradientText';
import { TouchableOpacity } from '../Components/common/Touchable';
import { DuelMissionProgress } from '../Components/Duels/DuelMissionProgress';
import { rewardTitle } from '../Components/Duels/DuelRewardUi';
import { DuelStatTile } from '../Components/Duels/DuelStatCards';
import { duelText } from '../Components/Duels/duelsText';
import { duelColors, formatScore } from '../Components/Duels/duelsTheme';
import {
	DuelButton,
	DuelClockText,
	DuelFinishedFigures,
	DuelLoading,
	DuelNotice,
	DuelPanel,
	DuelPill,
	DuelsPage,
	duelTextStyles,
} from '../Components/Duels/DuelsUi';
import { formatCountdown, useCountdown } from '../Components/Tournaments/useCountdown';
import { RootStackParamList } from '../navigator';

type NavType = NativeStackNavigationProp<RootStackParamList>;
type ResultRoute = RouteProp<RootStackParamList, 'DuelResult'>;

const COMPLETE_FIGURE_SLOTS = 6;

const LeaderboardRow = ({ row, scale }: { row: DuelResultLeaderboardEntryDto; scale: number }) => {
	const { t } = useTranslation();
	const avatar = row.avatarFileId ? { uri: getAvatarUrl(row.avatarFileId) } : PlayerIconPng;

	return (
		<View
			style={[
				styles.row,
				styles.leaderRow,
				{
					columnGap: 10 * scale,
					paddingHorizontal: 10 * scale,
					height: 44 * scale,
					borderRadius: 8 * scale,
					backgroundColor: row.isYou ? duelColors.youRow : duelColors.row,
					borderWidth: row.isYou ? Math.max(1, 1 * scale) : 0,
					borderColor: duelColors.youBorder,
				},
			]}
		>
			<Text style={[duelTextStyles.title, { width: 28 * scale, fontSize: 13 * scale }]}>
				{row.rank}
			</Text>
			<View>
				<Image
					source={avatar}
					style={{ width: 28 * scale, height: 28 * scale, borderRadius: 14 * scale }}
				/>
				{row.isYou ? (
					<View style={[styles.crown, { top: -9 * scale }]}>
						<DuelIconCrownSvg width={12 * scale} height={10 * scale} />
					</View>
				) : null}
			</View>
			<Text numberOfLines={1} style={[duelTextStyles.title, styles.flex, { fontSize: 14 * scale }]}>
				{row.isYou ? duelText(t, 'T_SITE_DUELS_YOU') : (row.username ?? '—')}
			</Text>
			<Text style={[duelTextStyles.title, { fontSize: 14 * scale }]}>
				{formatScore(row.displayScore)}
			</Text>
		</View>
	);
};

type BodyProps = {
	result: DuelResultResponse | null;
	push: DuelSnapshotDto | null;
	width: number;
	scale: number;
	onBack: () => void;
	onBreakdown: () => void;
	onOpenSeason: () => void;
};

const ProvisionalBody = ({ result, push, width, scale, onBack, onBreakdown }: BodyProps) => {
	const { t } = useTranslation();
	const expiresAt = result?.expiresAt ?? push?.expiresAt ?? null;
	const remaining = useCountdown(expiresAt ? new Date(expiresAt).getTime() : null);
	const score = result?.displayScore ?? push?.currentPlayerDisplayScore ?? null;
	const rank = push?.currentPlayerRank ?? result?.rank ?? null;
	const participants = result?.participantCount ?? push?.participantCount ?? 0;
	const finished = result?.finishedCount ?? push?.finishedCount ?? 0;

	const half = (width - 24 * scale - 17 * scale) / 2;
	return (
		<>
			<View style={[styles.hero, { width, rowGap: 2 * scale }]}>
				<Image
					source={DuelCompleteTrophyJpg}
					style={{ width: 200 * scale, height: 158 * scale, borderRadius: 16 * scale }}
					resizeMode='cover'
				/>
				<Text style={[duelTextStyles.title, { fontSize: 18 * scale }]}>
					{duelText(t, 'T_SITE_DUELS_GREAT_RUN')}
				</Text>
				<GoldGradientText
					text={formatScore(score)}
					width={width}
					height={50 * scale}
					fontSize={46 * scale}
					fontWeight='900'
				/>
				<Text style={[duelTextStyles.body, { fontSize: 14 * scale }]}>
					{duelText(t, 'T_SITE_DUELS_SCORE')}
				</Text>
			</View>

			<DuelPanel scale={scale} width={width * 0.8}>
				<View style={[styles.row, styles.between, { columnGap: 12 * scale }]}>
					<View style={[styles.center, styles.flex]}>
						<Text style={[duelTextStyles.gold, { fontSize: 28 * scale }]}>
							{rank ? `#${rank}` : duelText(t, 'T_SITE_DUELS_NOT_RANKED')}
						</Text>
						<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
							{rank
								? duelText(t, 'T_SITE_DUELS_CURRENT_POSITION')
								: duelText(t, 'T_SITE_DUELS_NOT_RANKED_BODY')}
						</Text>
					</View>
					<DuelPill label={duelText(t, 'T_SITE_DUELS_PROVISIONAL')} scale={scale} />
				</View>
			</DuelPanel>

			<DuelPanel scale={scale} width={width}>
				<View style={[styles.row, { columnGap: 8 * scale }]}>
					<View style={[{ width: half, rowGap: 6 * scale }]}>
						<Text style={[duelTextStyles.body, { fontSize: 13 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_PLAYERS_FINISHED', [finished, participants])}
						</Text>
						<DuelFinishedFigures
							finished={finished}
							participants={participants}
							slots={COMPLETE_FIGURE_SLOTS}
							scale={scale}
						/>
					</View>
					<View style={styles.divider} />
					<View style={[{ width: half, rowGap: 4 * scale }]}>
						{expiresAt ? (
							<DuelClockText
								text={duelText(t, 'T_SITE_DUELS_REMAINING', [formatCountdown(remaining)])}
								scale={scale}
							/>
						) : null}
						<Text style={[duelTextStyles.body, { fontSize: 11 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_FINAL_WHEN_CLOSES')}
						</Text>
					</View>
				</View>
			</DuelPanel>

			{result?.leaderboard.length ? (
				<DuelPanel scale={scale} width={width}>
					<View style={[styles.row, styles.between, { paddingHorizontal: 10 * scale }]}>
						<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_LEADERBOARD_PROVISIONAL')}
						</Text>
						<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_SCORE')}
						</Text>
					</View>
					{result.leaderboard.map(row => (
						<LeaderboardRow key={row.playerId} row={row} scale={scale} />
					))}
				</DuelPanel>
			) : null}

			<DuelButton
				label={duelText(t, 'T_SITE_DUELS_BACK_TO_DUELS')}
				onPress={onBack}
				width={width}
				scale={scale}
			/>
			{result?.breakdown ? (
				<DuelButton
					label={duelText(t, 'T_SITE_DUELS_VIEW_BREAKDOWN')}
					onPress={onBreakdown}
					width={width}
					scale={scale}
					variant='dark'
				/>
			) : null}
		</>
	);
};

const SettledBody = ({
	result,
	push,
	width,
	scale,
	onBack,
	onBreakdown,
	onOpenSeason,
}: BodyProps) => {
	const { t } = useTranslation();
	const settlement = result?.settlement ?? null;
	const rank = result?.rank ?? push?.currentPlayerRank ?? null;
	const score = result?.displayScore ?? push?.currentPlayerDisplayScore ?? null;
	const ratingDelta = settlement
		? settlement.ratingDelta
		: (push?.currentPlayerComputedRatingDelta ?? null);
	const pointsDelta = settlement
		? settlement.seasonPointsDelta
		: (push?.currentPlayerSeasonPoints ?? null);
	const placement = settlement?.placementReward ?? null;
	const tileWidth = (width - 24 * scale - 8 * scale) / 2;
	const nowText = (after: number | null) =>
		after === null ? null : duelText(t, 'T_SITE_DUELS_NOW', [formatScore(after)]);
	const rewards = [
		placement?.chips
			? {
					image: DuelChipStackPng,
					text: duelText(t, 'T_SITE_DUELS_REWARD_CHIPS', [formatScore(placement.chips)]),
				}
			: null,
		placement?.experience
			? {
					image: DuelXpJpg,
					text: duelText(t, 'T_SITE_DUELS_REWARD_XP', [formatScore(placement.experience)]),
				}
			: null,
	].filter((reward): reward is { image: number; text: string } => reward !== null);

	return (
		<>
			<DuelPanel scale={scale} width={width} highlighted style={styles.flush}>
				<View style={[styles.banner, { height: (width * 363) / 1080, borderRadius: 12 * scale }]}>
					<Image source={DuelSettledBannerJpg} style={styles.heroBackdrop} resizeMode='cover' />
					<View style={[styles.bannerText, { width: width * 0.42 }]}>
						<Text style={[duelTextStyles.title, { fontSize: 18 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_YOU_FINISHED')}
						</Text>
						<Text
							numberOfLines={rank ? 1 : 2}
							adjustsFontSizeToFit
							style={[
								duelTextStyles.gold,
								styles.centerText,
								{ fontSize: (rank ? 40 : 18) * scale },
							]}
						>
							{rank ? `#${rank}` : duelText(t, 'T_SITE_DUELS_NOT_RANKED')}
						</Text>
					</View>
				</View>
			</DuelPanel>

			<DuelPanel scale={scale} width={width}>
				<View style={[styles.grid, { gap: 8 * scale }]}>
					<DuelStatTile
						icon={DuelScoreShieldPng}
						label={duelText(t, 'T_SITE_DUELS_DUEL_SCORE')}
						value={score}
						width={tileWidth}
						scale={scale}
					/>
					<DuelStatTile
						icon={DuelRatingShieldPng}
						label={duelText(t, 'T_SITE_DUELS_DUEL_RATING')}
						delta={ratingDelta}
						nowText={nowText(settlement?.ratingAfter ?? null)}
						width={tileWidth}
						scale={scale}
					/>
					<DuelStatTile
						icon={DuelStarPng}
						label={duelText(t, 'T_SITE_DUELS_SEASON_POINTS')}
						delta={pointsDelta}
						nowText={nowText(settlement?.seasonPointsAfter ?? null)}
						width={tileWidth}
						scale={scale}
					/>
				</View>
				{settlement ? null : (
					<Text style={[duelTextStyles.muted, { fontSize: 12 * scale }]}>
						{duelText(t, 'T_SITE_DUELS_SETTLING')}
					</Text>
				)}
				{result?.personalBest ? (
					<Text style={[duelTextStyles.gold, { fontSize: 13 * scale }]}>
						{duelText(t, 'T_SITE_DUELS_PERSONAL_BEST')}
					</Text>
				) : null}
			</DuelPanel>

			<DuelMissionProgress settledAt={result?.settledAt ?? null} width={width} scale={scale} />

			{rewards.length ? (
				<DuelPanel scale={scale} width={width}>
					<Text style={[duelTextStyles.title, { fontSize: 16 * scale }]}>
						{duelText(t, 'T_SITE_DUELS_REWARDS_OBTAINED')}
					</Text>
					<View style={[styles.row, { columnGap: 16 * scale }]}>
						{rewards.map(reward => (
							<View key={reward.text} style={[styles.row, { columnGap: 8 * scale }]}>
								<Image
									source={reward.image}
									style={{ width: 44 * scale, height: 36 * scale }}
									resizeMode='contain'
								/>
								<Text style={[duelTextStyles.body, { width: 72 * scale, fontSize: 13 * scale }]}>
									{reward.text}
								</Text>
							</View>
						))}
					</View>
				</DuelPanel>
			) : null}

			{settlement?.milestones
				.filter(milestone => !!rewardTitle(t, milestone))
				.map(milestone => (
					<TouchableOpacity
						key={milestone.milestoneId}
						onPress={onOpenSeason}
						disabled={milestone.rewardType === 'RealWorld' || milestone.status !== 'Unlocked'}
						accessibilityRole='button'
					>
						<DuelPanel scale={scale} width={width}>
							<View style={[styles.row, { columnGap: 6 * scale }]}>
								<Image
									source={DuelCrownMedallionPng}
									style={{ width: 20 * scale, height: 20 * scale }}
									resizeMode='contain'
								/>
								<Text style={[duelTextStyles.gold, { fontSize: 12 * scale }]}>
									{duelText(t, 'T_SITE_DUELS_SEASON_MILESTONE')}
								</Text>
							</View>
							<Text style={[duelTextStyles.title, { fontSize: 15 * scale }]}>
								{duelText(t, 'T_SITE_DUELS_MILESTONE_UNLOCKED', [rewardTitle(t, milestone)])}
							</Text>
							{milestone.threshold !== null ? (
								<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
									{duelText(t, 'T_SITE_DUELS_MILESTONE_REACHED', [
										formatScore(milestone.threshold),
									])}
								</Text>
							) : null}
							{milestone.rewardType !== 'RealWorld' && milestone.status === 'Unlocked' ? (
								<Text style={[duelTextStyles.gold, { fontSize: 13 * scale }]}>
									{`${duelText(t, 'T_SITE_DUELS_CLAIM')} ›`}
								</Text>
							) : null}
						</DuelPanel>
					</TouchableOpacity>
				))}

			<DuelButton
				label={duelText(t, 'T_SITE_DUELS_BACK_TO_DUELS')}
				onPress={onBack}
				width={width}
				scale={scale}
			/>
			{result?.breakdown ? (
				<DuelButton
					label={duelText(t, 'T_SITE_DUELS_VIEW_BREAKDOWN')}
					onPress={onBreakdown}
					width={width}
					scale={scale}
					variant='dark'
				/>
			) : null}
		</>
	);
};

const DuelResultScreen = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigation = useNavigation<NavType>();
	const { duelId } = useRoute<ResultRoute>().params;
	const result = useSelector(selectDuelResult(duelId));
	const errorKey = useSelector(selectDuelResultErrorKey(duelId));
	const push = useSelector(selectDuelSnapshot(duelId));

	useEffect(() => {
		dispatch(duelsScreenMounted());
		return () => {
			dispatch(duelsScreenUnmounted());
		};
	}, [dispatch]);

	useFocusEffect(
		useCallback(() => {
			dispatch(duelResultFocus(duelId));
			return () => {
				dispatch(duelResultBlur(duelId));
			};
		}, [dispatch, duelId]),
	);

	const provisional = result ? result.provisional : push ? push.status !== 'completed' : true;
	const handleBack = () => navigation.navigate('Duels');
	const handleBreakdown = () => navigation.navigate('DuelScoreBreakdown', { duelId });

	return (
		<DuelsPage
			title={duelText(
				t,
				provisional ? 'T_SITE_DUELS_COMPLETE_TITLE' : 'T_SITE_DUELS_SETTLED_TITLE',
			)}
			onBack={handleBack}
		>
			{({ width, scale }) => {
				if (!result && errorKey) {
					return (
						<DuelNotice
							scale={scale}
							width={width}
							text={duelText(t, errorKey)}
							actionLabel={duelText(t, 'T_SITE_DUELS_RETRY')}
							onAction={() => dispatch(duelResultRetry(duelId))}
						/>
					);
				}
				if (!result && !push) return <DuelLoading />;

				const props = {
					result,
					push,
					width,
					scale,
					onBack: handleBack,
					onBreakdown: handleBreakdown,
					onOpenSeason: () => navigation.navigate('DuelSeason'),
				};
				return provisional ? <ProvisionalBody {...props} /> : <SettledBody {...props} />;
			}}
		</DuelsPage>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	between: {
		justifyContent: 'space-between',
	},
	flex: {
		flex: 1,
		minWidth: 0,
	},
	grid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
	},
	leaderRow: {
		alignSelf: 'stretch',
	},
	center: {
		alignItems: 'center',
	},
	crown: {
		position: 'absolute',
		alignSelf: 'center',
	},
	hero: {
		alignItems: 'center',
		overflow: 'hidden',
	},
	centerText: {
		textAlign: 'center',
	},
	heroBackdrop: {
		...StyleSheet.absoluteFillObject,
		width: undefined,
		height: undefined,
	},
	divider: {
		width: 1,
		alignSelf: 'stretch',
		backgroundColor: 'rgba(255,233,168,0.35)',
	},
	flush: {
		padding: 0,
	},
	banner: {
		overflow: 'hidden',
		justifyContent: 'center',
		alignItems: 'flex-end',
	},
	bannerText: {
		alignItems: 'center',
	},
});

export default DuelResultScreen;
